/**
 * Active-speaker tracking — who currently holds the main stage.
 *
 * The SFU runs a mediasoup `AudioLevelObserver` per room (500 ms interval,
 * top-5 entries, -60 dBFS threshold) and pushes the list to the signaling
 * server whenever it *changes*, which broadcasts it on the room channel:
 *
 *     "media:active_speakers" => {
 *       speakers: ["guest_abc", "guest_def"],          // loudest first
 *       volumes:  [{ peerId: "guest_abc", volume: -32 }, ...]
 *     }
 *
 * `speakers[0]` is the dominant speaker. `peerId` here is the Phoenix
 * `user_id` (= room-JWT `sub`), which is exactly the key Presence and the
 * consumer map use — so it lines up 1:1 with `Participant.id` with no mapping.
 *
 * Two behaviours matter for a stable layout and both live here rather than in
 * the render path:
 *
 *   - **Sticky.** A silent room emits `speakers: []`. We hold the last speaker
 *     instead of clearing, otherwise the main tile would drop back to the
 *     fallback peer during every pause between sentences.
 *   - **Min dwell.** Two people talking over each other flips `speakers[0]`
 *     twice a second. We refuse to switch the dominant speaker more often than
 *     `minDwellMs`, holding the newest candidate and committing it when the
 *     window expires.
 */

import { useEffect, useRef, useState } from 'react'
import type { PhoenixEventHandler } from './usePhoenixChannel'
import { vclog } from './vc-log'
import { createSpeakingRing } from '../services/speaking-ring'
import { speakerCandidate } from '../services/share-only-roster'

/** Minimum time the main stage keeps a speaker before it may switch again. */
const DEFAULT_MIN_DWELL_MS = 1500

export type ActiveSpeakersState = {
  /** Dominant speaker's peerId — sticky, min-dwell debounced. Null until the first speaker. */
  activeSpeakerId: string | null
  /** Raw loudest-first list from the last non-silent broadcast. */
  speakers: string[]
  /**
   * The active speaker while they can actually be heard — what the speaking
   * ring follows. Null once they have been quiet for 1.5 s, unlike
   * `activeSpeakerId`, which holds them through silence. See speaking-ring.ts.
   */
  speakingId: string | null
}

function sameOrder(a: string[], b: string[]): boolean {
  if (a.length !== b.length) return false
  for (let i = 0; i < a.length; i++) if (a[i] !== b[i]) return false
  return true
}

export function useActiveSpeakers(
  subscribe: (event: string, handler: PhoenixEventHandler) => () => void,
  minDwellMs: number = DEFAULT_MIN_DWELL_MS,
): ActiveSpeakersState {
  const [activeSpeakerId, setActiveSpeakerId] = useState<string | null>(null)
  const [speakers, setSpeakers] = useState<string[]>([])
  const [speakingId, setSpeakingId] = useState<string | null>(null)

  // Working copies so the debounce can decide without waiting on a render.
  const activeRef = useRef<string | null>(null)
  const lastSwitchAtRef = useRef(0)
  const pendingRef = useRef<string | null>(null)
  const timerRef = useRef<ReturnType<typeof setTimeout> | null>(null)
  /** Last speaker set printed, so a repeated broadcast does not reprint it. */
  const lastLoggedRef = useRef<string | null>(null)
  /** Latest audible list, so a delayed commit can tell whether its speaker is still talking. */
  const audibleRef = useRef<string[]>([])

  useEffect(() => {
    const ring = createSpeakingRing(setSpeakingId)

    const clearTimer = () => {
      if (timerRef.current) {
        clearTimeout(timerRef.current)
        timerRef.current = null
      }
    }

    const commit = (id: string) => {
      clearTimer()
      pendingRef.current = null
      activeRef.current = id
      lastSwitchAtRef.current = Date.now()
      setActiveSpeakerId(id)
      // A switch committed after the dwell lights its speaker at once if they
      // are still talking, rather than waiting for the next broadcast.
      ring.update(id, audibleRef.current)
      vclog('presence', 'active speaker', { peerId: id })
      // Ungated, like the lastN line: this is the other half of "why is the
      // grid not doing what I expect", and it has to be answerable on a
      // deployed meeting where `vc:debug` was never set.
      console.info('[activeSpeaker] committed', { peerId: id })
    }

    const consider = (candidate: string | null) => {
      // Silence, or the same person still talking — nothing to do. Drop any
      // pending switch: the candidate that was waiting is no longer loudest.
      if (candidate === null || candidate === activeRef.current) {
        pendingRef.current = null
        clearTimer()
        return
      }

      // First speaker of the meeting takes the stage immediately.
      const elapsed = Date.now() - lastSwitchAtRef.current
      if (activeRef.current === null || elapsed >= minDwellMs) {
        commit(candidate)
        return
      }

      // Inside the dwell window — remember the newest candidate and commit it
      // when the window closes (a later payload may replace it before then).
      pendingRef.current = candidate
      if (timerRef.current) return
      timerRef.current = setTimeout(() => {
        timerRef.current = null
        const next = pendingRef.current
        if (next && next !== activeRef.current) commit(next)
      }, minDwellMs - elapsed)
    }

    // Proof that the handler is installed at all.
    //
    // Without this, "no [activeSpeaker] event" has two completely different
    // causes that look identical from the console: the broadcast never arrived,
    // or this hook never subscribed (stale bundle, hook not mounted, socket not
    // joined yet). One is a server problem and one is not.
    console.info('[activeSpeaker] subscribed to media:active_speakers')

    const off = subscribe('media:active_speakers', (payload) => {
      const raw = Array.isArray(payload.speakers) ? (payload.speakers as unknown[]) : []
      const ids = raw.filter((s): s is string => typeof s === 'string' && s.length > 0)

      // Logged on ARRIVAL, before any filtering or debouncing.
      //
      // Until now the only log was in `commit`, which means "no broadcast ever
      // reached us" and "broadcasts arrive but nothing survives the filter"
      // produced exactly the same silence — and they need completely different
      // fixes, one in the SFU and one here. Printing the raw payload separates
      // them at a glance.
      // Only when the set actually CHANGES. The SFU broadcasts on a timer, so
      // this fired every second for the whole meeting and printed the same line
      // each time — console.info with an object payload is not free, least of
      // all with DevTools attached. A repeat says nothing the previous line did
      // not, while the first arrival and every change still print, which is
      // what this log exists to show.
      const key = ids.join(',')
      if (key !== lastLoggedRef.current) {
        lastLoggedRef.current = key
        console.info('[activeSpeaker] event', {
          speakers: ids,
          rawCount: raw.length,
          dominant: ids[0] ?? null,
        })
      }

      // Silence clears the raw list but never the sticky dominant speaker.
      setSpeakers((prev) => (sameOrder(prev, ids) ? prev : ids))
      audibleRef.current = ids
      // Never a screen-share session: the observer hears a share page's tab
      // sound, but it has no tile to spotlight, promote or put in PiP — the
      // person talking over it keeps the stage (services/share-only-roster).
      consider(speakerCandidate(ids))
      ring.update(activeRef.current, ids)
    })

    return () => {
      off()
      clearTimer()
      ring.dispose()
    }
  }, [subscribe, minDwellMs])

  return { activeSpeakerId, speakers, speakingId }
}
